import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { api } from '../hooks/useAuth';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [embedCode, setEmbedCode] = useState('');
  const [storeId, setStoreId] = useState('');
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('wordpress'); // wordpress, shopify, html

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch connection status
      const { data: status } = await api.get('/api/auth/facebook/status');

      // Fetch saved reels count
      const { data: reelsData } = await api.get('/api/auth/facebook/reels/saved');
      const reels = reelsData.reels || [];

      // Calculate stats
      const taggedCount = reels.reduce((sum, r) => {
        const tags = r.tagged_products?.filter(p => p.id).length || 0;
        return sum + tags;
      }, 0);

      setStats({
        totalReels: reels.length,
        taggedProducts: taggedCount,
        views: 0,
        conversions: 0,
        igConnected: status.ig_connected,
      });

      // Get embed store ID
      const { data: userData } = await api.get('/api/user/embed-id');
      if (userData.embed_store_id) {
        setStoreId(userData.embed_store_id);
        generateEmbedCode(userData.embed_store_id);
      }
    } catch (e) {
      console.error('Failed to load dashboard data');
    }
  };

  const generateEmbedCode = (id) => {
    const scriptUrl = 'https://vids.gristip.com/reels.js';

    const codes = {
      wordpress: `<?php
// Add to your theme's functions.php

// 1️⃣ Load VidsCommerce Script (safe + optimized)
function vidscommerce_load_script() {
    ?>
    <script 
        src="${scriptUrl}"
        data-store-id="<?php echo esc_attr(get_option('vidscommerce_store_id', '${id}')); ?>"
        defer>
    </script>
    <?php
}
add_action('wp_footer', 'vidscommerce_load_script', 100);

// 2️⃣ Homepage Slider (Woodmart specific hook)
function vidscommerce_homepage_slider() {
    if (!is_front_page()) return;
    echo '<div data-reels="homepage" style="margin-top:20px;"></div>';
}
add_action('woodmart_after_header', 'vidscommerce_homepage_slider', 20);

// 3️⃣ Product Page Floating Player
function vidscommerce_product_floating() {
    if (!is_product()) return;
    global $product;
    if (!$product) return;

    echo '<div 
            data-reels="pdp" 
            data-product-id="' . esc_attr($product->get_id()) . '"
          ></div>';
}
add_action('wp_footer', 'vidscommerce_product_floating', 20);

// 4️⃣ WooCommerce Nonce Fix
add_action('wp_footer', function () {
    ?>
    <script>
      window.wcBlocksStoreApiNonce = "<?php echo wp_create_nonce('wc_store_api'); ?>";
    </script>
    <?php
});

add_filter('woocommerce_store_api_disable_nonce_check', '__return_true');
?>`,

      shopify: `<!-- Add to theme.liquid before </body> -->

<script 
  src="${scriptUrl}"
  data-store-id="${id}"
  defer>
</script>

<!-- Homepage Story Slider -->
{% if template == 'index' %}
  <div data-reels="homepage"></div>
{% endif %}

<!-- Product Page -->
{% if template contains 'product' %}
  <div data-reels="pdp" data-product-id="{{ product.id }}"></div>
{% endif %}`,

      html: `<!-- Add to your HTML -->

<script 
  src="${scriptUrl}"
  data-store-id="${id}"
  defer>
</script>

<!-- Homepage -->
<div data-reels="homepage"></div>

<!-- Product Page -->
<div data-reels="pdp" data-product-id="PRODUCT_ID"></div>

<!-- Floating Mini Player -->
<div data-reels="floating"></div>`
    };

    setEmbedCode(codes);
  };

  const copyEmbedCode = () => {
    const code = embedCode[activeTab];
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">Welcome back — here's your shoppable reels overview</p>
          </div>
        </div>

        {/* Connection Status Banner */}
        {!stats?.igConnected && (
          <div className="alert alert-warning" style={{ marginBottom: 24 }}>
            ⚠️ Instagram not connected yet — go to Connect Accounts to get started
          </div>
        )}

        {/* Stats Grid */}
        <div className="stats-grid">
          <StatCard
            title="Total Reels"
            value={stats?.totalReels || 0}
            subtitle="Synced from Instagram"
            icon="🎬"
            color="var(--accent)"
          />
          <StatCard
            title="Tagged Products"
            value={stats?.taggedProducts || 0}
            subtitle="Tag products to reels"
            icon="🏷️"
            color="var(--accent2)"
          />
          <StatCard
            title="Views (7D)"
            value={stats?.views || 0}
            subtitle="Analytics coming soon"
            icon="👁️"
            color="var(--accent3)"
          />
          <StatCard
            title="Conversions"
            value={stats?.conversions || 0}
            subtitle="Add cart integration"
            icon="💰"
            color="var(--success)"
          />
        </div>

        {/* Quick Actions */}
        <div className="card" style={{ marginTop: 24, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Quick Actions</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <QuickAction icon="🔗" label="Connect Instagram" href="/connect" />
            <QuickAction icon="🎬" label="Manage Reels" href="/reels" />
            <QuickAction icon="⚙️" label="Manual Connect" href="/manual-connect" />
            <QuickAction icon="▶️" label="Preview Player" href="/player" />
          </div>
        </div>

        {/* Embed Code */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700 }}>Embed Code</h3>
              <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 2 }}>
                Copy and paste this code into your website
              </p>
            </div>
            <button
              className={`btn ${copied ? 'btn-success' : 'btn-primary'} btn-sm`}
              onClick={copyEmbedCode}
              disabled={!embedCode}
            >
              {copied ? '✓ Copied!' : '📋 Copy Code'}
            </button>
          </div>

          {embedCode ? (
            <>
              {/* Platform Tabs */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
                {[
                  ['wordpress', '🔷 WordPress'],
                  ['shopify', '🛍️ Shopify'],
                  ['html', '📄 HTML']
                ].map(([key, label]) => (
                  <button
                    key={key}
                    className={`tab-button ${activeTab === key ? 'active' : ''}`}
                    onClick={() => setActiveTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Code Block */}
              <pre className="code-block" style={{ maxHeight: 400, overflowY: 'auto' }}>
                {embedCode[activeTab]}
              </pre>

              {/* Store ID Display */}
              {storeId && (
                <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--bg)', borderRadius: 8, fontSize: 12 }}>
                  <div style={{ color: 'var(--text-dim)', marginBottom: 4 }}>Your Store ID:</div>
                  <code style={{ color: 'var(--accent)', fontWeight: 600 }}>{storeId}</code>
                </div>
              )}

              {/* Instructions Based on Platform */}
              <div style={{ marginTop: 16, padding: '16px', background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)' }}>
                {activeTab === 'wordpress' && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>📚 WordPress Installation:</h4>
                    <ol style={{ fontSize: 12, color: 'var(--text-dim)', paddingLeft: 20, margin: 0, lineHeight: 1.8 }}>
                      <li>Go to <strong>Appearance → Theme File Editor</strong></li>
                      <li>Open <strong>functions.php</strong></li>
                      <li>Paste the code at the bottom of the file</li>
                      <li>Click <strong>Update File</strong></li>
                      <li>Visit your homepage - reels will appear after the header</li>
                      <li>Visit any product page - floating player will appear</li>
                    </ol>
                    <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(255, 214, 10, 0.1)', borderRadius: 6, fontSize: 12 }}>
                      ⚠️ <strong>Note:</strong> If not using Woodmart theme, change <code>woodmart_after_header</code> to <code>wp_footer</code> or your theme's action hook
                    </div>
                  </>
                )}

                {activeTab === 'shopify' && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>📚 Shopify Installation:</h4>
                    <ol style={{ fontSize: 12, color: 'var(--text-dim)', paddingLeft: 20, margin: 0, lineHeight: 1.8 }}>
                      <li>Go to <strong>Online Store → Themes → Edit Code</strong></li>
                      <li>Open <strong>theme.liquid</strong></li>
                      <li>Paste code before <code>&lt;/body&gt;</code> tag</li>
                      <li>Click <strong>Save</strong></li>
                      <li>Reels will automatically show on homepage and product pages</li>
                    </ol>
                  </>
                )}

                {activeTab === 'html' && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>📚 HTML Installation:</h4>
                    <ul style={{ fontSize: 12, color: 'var(--text-dim)', paddingLeft: 20, margin: 0, lineHeight: 1.8 }}>
                      <li><strong>Script:</strong> Add before <code>&lt;/body&gt;</code> tag</li>
                      <li><strong>Homepage slider:</strong> Use <code>data-reels="homepage"</code></li>
                      <li><strong>Product page:</strong> Use <code>data-reels="pdp"</code> with <code>data-product-id</code></li>
                      <li><strong>Floating player:</strong> Use <code>data-reels="floating"</code> for bottom-right mini player</li>
                      <li><strong>Collection page:</strong> Use <code>data-reels="collection"</code> for category pages</li>
                    </ul>
                  </>
                )}
              </div>

            </>
          ) : (
            <div className="empty-state">
              Connect Instagram first to generate your embed code
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, color }) {
  return (
    <div className="stat-card">
      <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{subtitle}</div>
    </div>
  );
}

function QuickAction({ icon, label, href }) {
  return (
    <a href={href} className="quick-action">
      <span style={{ fontSize: 24 }}>{icon}</span>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
    </a>
  );
}
